"use client";

import { useState } from "react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";

export default function ConfiguracionCookies() {
  const [preferences, setPreferences] = useState({
    necessary: true, // Always enabled
    analytics: true,
    marketing: false,
    functionality: true
  });

  const handleToggle = (type: keyof typeof preferences) => {
    if (type === 'necessary') return; // Can't disable necessary cookies
    
    setPreferences(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  const handleSavePreferences = () => {
    // Save to localStorage
    localStorage.setItem('cookiePreferences', JSON.stringify(preferences));
    alert('Preferencias de cookies guardadas exitosamente');
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      functionality: true
    };
    setPreferences(allAccepted);
    localStorage.setItem('cookiePreferences', JSON.stringify(allAccepted));
    alert('Todas las cookies han sido aceptadas');
  };

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      marketing: false,
      functionality: false
    };
    setPreferences(onlyNecessary);
    localStorage.setItem('cookiePreferences', JSON.stringify(onlyNecessary));
    alert('Solo las cookies necesarias han sido aceptadas');
  };

  return (
    <>
      <Navbar />
      <main className="min-h-screen bg-white">
        <div className="max-w-4xl mx-auto px-4 py-12 md:py-16 pt-24 md:pt-28">
          <div className="prose prose-lg max-w-none">
            <h1 className="text-4xl font-bold text-gray-900 mb-8">Configuración de Cookies</h1>
            
            <p className="text-gray-600 mb-8">
              Gestiona tus preferencias de cookies para personalizar tu experiencia en Vehículos NDC.
            </p>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">¿Qué son las cookies?</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Las cookies son pequeños archivos de texto que se almacenan en su dispositivo cuando visita nuestro sitio web. Nos ayudan a mejorar su experiencia de navegación, recordar sus preferencias y proporcionar contenido personalizado.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Configurar Preferencias</h2>
              
              {/* Necessary Cookies */}
              <div className="bg-gray-50 p-6 rounded-lg mb-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-800">Cookies Necesarias</h3>
                    <p className="text-gray-600 text-sm">Requeridas para el funcionamiento básico del sitio</p>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={preferences.necessary}
                      disabled
                      className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label className="ml-2 text-sm text-gray-500">Siempre activo</label>
                  </div>
                </div>
                <p className="text-gray-700 text-sm">
                  Estas cookies son esenciales para que pueda navegar por el sitio web y utilizar sus funciones. Sin estas cookies, no podemos proporcionar servicios básicos como la navegación segura y el acceso a áreas protegidas.
                </p>
              </div>

              {/* Analytics Cookies */}
              <div className="bg-gray-50 p-6 rounded-lg mb-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-800">Cookies de Análisis</h3>
                    <p className="text-gray-600 text-sm">Nos ayudan a entender cómo usa el sitio web</p>
                  </div>
                  <div className="flex items-center">
                    <button
                      onClick={() => handleToggle('analytics')}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        preferences.analytics ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          preferences.analytics ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>
                <p className="text-gray-700 text-sm">
                  Estas cookies recopilan información sobre cómo los visitantes usan nuestro sitio web, qué páginas visitan más y si reciben mensajes de error. Esta información nos ayuda a mejorar nuestro sitio web.
                </p>
              </div>

              {/* Marketing Cookies */}
              <div className="bg-gray-50 p-6 rounded-lg mb-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-800">Cookies de Marketing</h3>
                    <p className="text-gray-600 text-sm">Para mostrar anuncios relevantes y personalizados</p>
                  </div>
                  <div className="flex items-center">
                    <button
                      onClick={() => handleToggle('marketing')}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        preferences.marketing ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          preferences.marketing ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>
                <p className="text-gray-700 text-sm">
                  Estas cookies se utilizan para mostrar anuncios que son relevantes para usted y sus intereses. También pueden usarse para limitar el número de veces que ve un anuncio y medir la efectividad de las campañas publicitarias.
                </p>
              </div>

              {/* Functionality Cookies */}
              <div className="bg-gray-50 p-6 rounded-lg mb-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-800">Cookies de Funcionalidad</h3>
                    <p className="text-gray-600 text-sm">Permiten funciones mejoradas y personalización</p>
                  </div>
                  <div className="flex items-center">
                    <button
                      onClick={() => handleToggle('functionality')}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        preferences.functionality ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          preferences.functionality ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>
                <p className="text-gray-700 text-sm">
                  Estas cookies permiten que el sitio web recuerde las opciones que hace (como su nombre de usuario, idioma o región) y proporcione funciones mejoradas y más personales.
                </p>
              </div>
            </section>

            {/* Action Buttons */}
            <section className="mb-8">
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={handleSavePreferences}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                >
                  Guardar Preferencias
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="bg-green-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-green-700 transition-colors"
                >
                  Aceptar Todas
                </button>
                <button
                  onClick={handleRejectAll}
                  className="bg-gray-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-gray-700 transition-colors"
                >
                  Solo Necesarias
                </button>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Información Adicional</h2>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3">Gestión de Cookies en su Navegador</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                También puede gestionar las cookies directamente desde la configuración de su navegador web. La mayoría de navegadores le permiten:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Ver qué cookies están almacenadas</li>
                <li>Eliminar cookies individualmente o todas</li>
                <li>Bloquear cookies de sitios específicos</li>
                <li>Bloquear cookies de terceros</li>
                <li>Eliminar todas las cookies al cerrar el navegador</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">Cookies de Terceros</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Algunos de nuestros socios pueden colocar cookies en su dispositivo cuando visita nuestro sitio. Esto incluye:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li><strong>Google Analytics:</strong> Para análisis de tráfico web</li>
                <li><strong>Facebook Pixel:</strong> Para publicidad personalizada</li>
                <li><strong>Servicios de Chat:</strong> Para soporte al cliente en tiempo real</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">Actualizaciones</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Podemos actualizar esta página ocasionalmente para reflejar cambios en nuestro uso de cookies. Le recomendamos revisar esta página periódicamente.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Contacto</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Si tiene preguntas sobre nuestro uso de cookies, contáctenos:
              </p>
              <div className="bg-gray-50 p-6 rounded-lg">
                <p className="text-gray-700 mb-2"><strong>Vehículos NDC</strong></p>
                <p className="text-gray-700 mb-2">Email: cookies@vehiculosndc.com</p>
                <p className="text-gray-700 mb-2">Teléfono: [Número de contacto]</p>
                <p className="text-gray-700">Dirección: [Dirección física]</p>
              </div>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}